import { useState, useRef, useEffect, type ReactNode } from 'react'
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCorners,
  type DragEndEvent, type DragOverEvent,
} from '@dnd-kit/core'
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export type PanelId = string
export interface PanelLayout {
  col1: PanelId[]
  col2: PanelId[]
  rowFull: PanelId[]
}

const STORAGE_KEY = 'jamie-dashboard:panel-layout'

function loadLayout(defaultLayout: PanelLayout): PanelLayout {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return defaultLayout
    const parsed = JSON.parse(raw) as Partial<PanelLayout>
    // Merge with default to recover from corrupted state
    return {
      col1:    Array.isArray(parsed.col1)    ? parsed.col1    : defaultLayout.col1,
      col2:    Array.isArray(parsed.col2)    ? parsed.col2    : defaultLayout.col2,
      rowFull: Array.isArray(parsed.rowFull) ? parsed.rowFull : defaultLayout.rowFull,
    }
  } catch { return defaultLayout }
}
function saveLayout(l: PanelLayout) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(l)) } catch { /* ignore */ }
}

export interface PanelSize {
  height?: number          // px
  wide?: boolean           // span both columns
}
interface SortablePanelProps {
  id: PanelId
  size?: PanelSize
  onSizeChange: (id: PanelId, next: PanelSize) => void
  children: ReactNode
}
export function SortablePanel({ id, size, onSizeChange, children }: SortablePanelProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id })
  const ref = useRef<HTMLDivElement | null>(null)
  const dragStart = useRef<{ x: number; y: number; w: number; h: number; wide: boolean } | null>(null)
  const [resizing, setResizing] = useState(false)

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : (resizing ? 'none' : transition),
    opacity: isDragging ? 0.45 : 1,
    zIndex: isDragging ? 10 : (resizing ? 5 : 'auto'),
    position: 'relative',
    height: size?.height ? `${size.height}px` : 'auto',
    gridColumn: size?.wide ? '1 / -1' : 'auto',
  }

  function onResizePointerDown(e: React.PointerEvent) {
    e.preventDefault()
    e.stopPropagation()
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    dragStart.current = {
      x: e.clientX, y: e.clientY,
      w: rect.width, h: rect.height,
      wide: !!size?.wide,
    }
    setResizing(true)
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
  }
  function onResizePointerMove(e: React.PointerEvent) {
    const start = dragStart.current
    if (!start) return
    const dx = e.clientX - start.x
    const dy = e.clientY - start.y
    // Width: snap. >120px right → wide; <-120px left → narrow.
    let nextWide = start.wide
    if (!start.wide && dx > 120) nextWide = true
    else if (start.wide && dx < -120) nextWide = false
    // Height: free, min 80, max 1400.
    const nextH = Math.max(80, Math.min(1400, Math.round(start.h + dy)))
    onSizeChange(id, { height: nextH, wide: nextWide })
  }
  function onResizePointerUp(e: React.PointerEvent) {
    dragStart.current = null
    setResizing(false)
    ;(e.target as Element).releasePointerCapture?.(e.pointerId)
  }

  function setRefs(node: HTMLDivElement | null) {
    setNodeRef(node)
    ref.current = node
  }

  return (
    <div ref={setRefs} style={style}
         className={`sortable-panel ${isDragging ? 'dragging' : ''} ${resizing ? 'resizing' : ''} ${size?.wide ? 'wide' : ''}`}
         data-panel-id={id}>
      <button
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className="panel-drag-handle"
        aria-label="Drag panel">
        ⋮⋮
      </button>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {children}
      </div>
      <div
        className="panel-resize-handle"
        title="Drag to resize · drag right to widen across columns"
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        onPointerCancel={onResizePointerUp}>
        <span className="panel-resize-glyph">⤡</span>
      </div>
    </div>
  )
}

interface Props {
  defaultLayout: PanelLayout
  panels: Record<PanelId, ReactNode>
}

const SIZES_KEY = 'jamie-dashboard:panel-sizes'

export default function SortableLayout({ defaultLayout, panels }: Props) {
  const [sizes, setSizes] = useState<Record<PanelId, PanelSize>>(() => {
    try {
      const raw = localStorage.getItem(SIZES_KEY)
      return raw ? (JSON.parse(raw) as Record<PanelId, PanelSize>) : {}
    } catch { return {} }
  })
  // Throttle persistance during a drag — write at most every 200ms; final
  // size always lands on pointer-up.
  const persistTimer = useRef<number | null>(null)
  function updateSize(id: PanelId, next: PanelSize) {
    setSizes(prev => {
      const merged = { ...prev, [id]: { ...(prev[id] ?? {}), ...next } }
      if (persistTimer.current) window.clearTimeout(persistTimer.current)
      persistTimer.current = window.setTimeout(() => {
        try { localStorage.setItem(SIZES_KEY, JSON.stringify(merged)) } catch { /* ignore */ }
      }, 200)
      return merged
    })
  }

  const [layout, setLayout] = useState<PanelLayout>(() => {
    // Reconcile: any panel id in `panels` that's not in saved layout joins col1
    const saved = loadLayout(defaultLayout)
    const known = new Set([...saved.col1, ...saved.col2, ...saved.rowFull])
    const missing = Object.keys(panels).filter(id => !known.has(id))
    let next: PanelLayout
    if (missing.length > 0) {
      // New panels: ones flagged urgent go to the TOP of col1, others to the bottom
      const top = missing.filter(id => id === 'urgent')
      const bottom = missing.filter(id => id !== 'urgent')
      next = { ...saved, col1: [...top, ...saved.col1, ...bottom] }
    } else {
      const valid = (arr: PanelId[]) => arr.filter(id => panels[id] !== undefined)
      next = { col1: valid(saved.col1), col2: valid(saved.col2), rowFull: valid(saved.rowFull) }
    }
    // Always pin "urgent" to position 0 of col1 — too important to bury
    if (next.col1.includes('urgent') && next.col1[0] !== 'urgent') {
      next = { ...next, col1: ['urgent', ...next.col1.filter(id => id !== 'urgent')] }
    }
    saveLayout(next)
    return next
  })

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  function findContainer(id: PanelId): keyof PanelLayout | null {
    if (layout.col1.includes(id))    return 'col1'
    if (layout.col2.includes(id))    return 'col2'
    if (layout.rowFull.includes(id)) return 'rowFull'
    return null
  }

  function commit(next: PanelLayout) {
    setLayout(next)
    saveLayout(next)
  }

  // While dragging across containers, dnd-kit fires `onDragOver` repeatedly.
  // We move the dragged item between arrays so the layout previews correctly.
  function handleDragOver(e: DragOverEvent) {
    const { active, over } = e
    if (!over) return
    const activeId = String(active.id)
    const overId   = String(over.id)
    const fromCol  = findContainer(activeId)
    const toCol    = findContainer(overId) ?? (Object.keys(layout) as Array<keyof PanelLayout>).find(k => k === overId)
    if (!fromCol || !toCol || fromCol === toCol) return

    setLayout(prev => {
      const fromArr = [...prev[fromCol]]
      const toArr   = [...prev[toCol as keyof PanelLayout]]
      const fromIdx = fromArr.indexOf(activeId)
      if (fromIdx < 0) return prev
      fromArr.splice(fromIdx, 1)
      const overIdxInTarget = toArr.indexOf(overId)
      const insertAt = overIdxInTarget >= 0 ? overIdxInTarget : toArr.length
      toArr.splice(insertAt, 0, activeId)
      return { ...prev, [fromCol]: fromArr, [toCol]: toArr }
    })
  }

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (!over) return saveLayout(layout)
    const activeId = String(active.id)
    const overId   = String(over.id)
    const col      = findContainer(activeId)
    if (!col) return saveLayout(layout)
    const arr      = layout[col]
    const from     = arr.indexOf(activeId)
    const to       = arr.indexOf(overId)
    if (from < 0 || to < 0 || from === to) return saveLayout(layout)
    commit({ ...layout, [col]: arrayMove(arr, from, to) })
  }

  const renderPanel = (id: PanelId) => panels[id] ? (
    <SortablePanel key={id} id={id} size={sizes[id]} onSizeChange={updateSize}>
      {panels[id]}
    </SortablePanel>
  ) : null

  // Suppress the unused-variable warning for useEffect import; we don't need it
  // for layout rendering — kept for future side-effects without re-importing.
  void useEffect

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners}
                onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
      <div className="layout-cols">
        <SortableContext items={layout.col1} strategy={verticalListSortingStrategy}>
          <div className="layout-col">{layout.col1.map(renderPanel)}</div>
        </SortableContext>
        <SortableContext items={layout.col2} strategy={verticalListSortingStrategy}>
          <div className="layout-col">{layout.col2.map(renderPanel)}</div>
        </SortableContext>
        {layout.rowFull.length > 0 && (
          <SortableContext items={layout.rowFull} strategy={verticalListSortingStrategy}>
            <div className="layout-row-full home">{layout.rowFull.map(renderPanel)}</div>
          </SortableContext>
        )}
      </div>
    </DndContext>
  )
}
